import React from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { FilterChip } from "./FilterChip";
import { useThemeColor } from "@/hooks/useThemeColor";
import { IContactFilterCriteria, GroupModel } from "common247";

export interface IContactFilterComponentProps {
  criteria: IContactFilterCriteria;
  onChange: (updated: IContactFilterCriteria) => void;
  allGroups?: GroupModel[];
}

const ROLES = [
  { key: "all", label: "All Roles" },
  { key: "owner", label: "Owner" },
  { key: "editor", label: "Editor" },
  { key: "viewer", label: "Viewer" },
];

const LINKED_STATES = [
  { key: "all", label: "All Contacts" },
  { key: "linked", label: "Registered / Linked" },
  { key: "unlinked", label: "Unlinked Only" },
];

/**
 * Filter controls specific to Contact entities.
 */
export function ContactFilter({
  criteria,
  onChange,
  allGroups = [],
}: IContactFilterComponentProps): React.JSX.Element {
  const textMuted = useThemeColor({}, "textMuted");

  const currentRoles = criteria.userRoles || [];
  const isLinked = criteria.isLinked;
  const currentGroupIds = criteria.groupIds || [];

  const handleRoleToggle = (roleKey: string): void => {
    if (roleKey === "all") {
      onChange({ ...criteria, userRoles: undefined });
      return;
    }
    const updated = currentRoles.includes(roleKey)
      ? currentRoles.filter((r) => r !== roleKey)
      : [...currentRoles, roleKey];
    onChange({ ...criteria, userRoles: updated.length > 0 ? updated : undefined });
  };

  const handleGroupToggle = (groupId: string): void => {
    const updated = currentGroupIds.includes(groupId)
      ? currentGroupIds.filter((id) => id !== groupId)
      : [...currentGroupIds, groupId];
    onChange({ ...criteria, groupIds: updated.length > 0 ? updated : undefined });
  };

  return (
    <View style={styles.container}>
      {/* Account / Linked State */}
      <View style={styles.section}>
        <ThemedText style={[styles.label, { color: textMuted }]}>Account Status</ThemedText>
        <View style={styles.chipsWrap}>
          {LINKED_STATES.map((item) => {
            const isSelected =
              item.key === "all"
                ? isLinked === undefined
                : item.key === "linked"
                ? isLinked === true
                : isLinked === false;
            return (
              <FilterChip
                key={item.key}
                label={item.label}
                selected={isSelected}
                onPress={() => {
                  if (item.key === "all") onChange({ ...criteria, isLinked: undefined });
                  else if (item.key === "linked") onChange({ ...criteria, isLinked: true });
                  else onChange({ ...criteria, isLinked: false });
                }}
                compact
              />
            );
          })}
        </View>
      </View>

      {/* User Roles */}
      <View style={styles.section}>
        <ThemedText style={[styles.label, { color: textMuted }]}>Permissions / Roles</ThemedText>
        <View style={styles.chipsWrap}>
          {ROLES.map((r) => {
            const isSelected = r.key === "all" ? currentRoles.length === 0 : currentRoles.includes(r.key);
            return (
              <FilterChip
                key={r.key}
                label={r.label}
                selected={isSelected}
                onPress={() => handleRoleToggle(r.key)}
                compact
              />
            );
          })}
        </View>
      </View>

      {/* Member of Groups */}
      {allGroups.length > 0 && (
        <View style={styles.section}>
          <ThemedText style={[styles.label, { color: textMuted }]}>Group Membership</ThemedText>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.horizScroll}>
            {allGroups.map((g) => (
              <FilterChip
                key={g.id}
                label={g.name}
                icon="people-outline"
                selected={currentGroupIds.includes(g.id || "")}
                onPress={() => g.id && handleGroupToggle(g.id)}
                compact
              />
            ))}
          </ScrollView>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: 4,
  },
  section: {
    marginBottom: 8,
  },
  label: {
    fontSize: 12,
    fontWeight: "600",
    marginBottom: 4,
  },
  chipsWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  horizScroll: {
    flexDirection: "row",
  },
});
